/**
 * @fileoverview Shared logging utilities
 */

const consoleRef = typeof console !== 'undefined' ? console : null;

/**
 * Safely stringify values for logging without crashing on circular references.
 * @param {unknown} value
 * @returns {string}
 */
function safeStringify(value) {
  if (value === undefined) {
    return 'undefined';
  }

  const cycleGuard = new WeakSet();

  const replacer = (_key, current) => {
    if (current === undefined) {
      return 'undefined';
    }

    if (typeof current === 'object' && current !== null) {
      if (cycleGuard.has(current)) {
        return '[Circular]';
      }
      cycleGuard.add(current);
    }

    return current;
  };

  try {
    probeForSerialization(value, new WeakSet());
    const result = JSON.stringify(value, replacer);
    return typeof result === 'undefined' ? 'undefined' : result;
  } catch (_error) {
    return '[Unserializable]';
  }
}

/**
 * Process a property descriptor and recursively probe its value.
 * @param {PropertyDescriptor} descriptor
 * @param {object} target - The object owning the property
 * @param {WeakSet} seen - Circular reference guard
 */
function processDescriptor(descriptor, target, seen) {
  if (!descriptor) {
    return;
  }

  const isGetter =
    typeof descriptor.get === 'function' &&
    !Object.prototype.hasOwnProperty.call(descriptor, 'value');

  if (isGetter) {
    const nextValue = descriptor.get.call(target);
    probeForSerialization(nextValue, seen);
  } else if (Object.prototype.hasOwnProperty.call(descriptor, 'value')) {
    probeForSerialization(descriptor.value, seen);
  }
}

function probeForSerialization(target, seen) {
  if (target === null || typeof target !== 'object') {
    return;
  }

  if (seen.has(target)) {
    return;
  }
  seen.add(target);

  if (typeof target === 'function') {
    return;
  }

  if (seen.size > 1000) {
    return;
  }

  const descriptors = Object.getOwnPropertyDescriptors(target);
  for (const key of Object.keys(descriptors)) {
    processDescriptor(descriptors[key], target, seen);
  }

  const symbolKeys = Object.getOwnPropertySymbols ? Object.getOwnPropertySymbols(target) : [];
  for (const symbolKey of symbolKeys) {
    const descriptor = Object.getOwnPropertyDescriptor(target, symbolKey);
    processDescriptor(descriptor, target, seen);
  }
}

/**
 * Determine whether logs should be no-op (production) or verbose (development).
 * Webpack defines __PRODUCTION__ at build time.
 */
const isProduction =
  typeof __PRODUCTION__ !== 'undefined'
    ? Boolean(__PRODUCTION__)
    : typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';

const noop = () => {};

function formatMessage(level, context, message) {
  const ctx = context || 'SYSTEM';
  const msg = message ?? '';
  return `[${level}:${ctx}] ${msg}`;
}

function logWith(method, level, context, message, error) {
  if (!consoleRef || typeof consoleRef[method] !== 'function') {
    return;
  }

  const payloadMessage = error?.message || error || message;
  consoleRef[method](formatMessage(level, context, payloadMessage));

  if (error instanceof Error && error.stack) {
    consoleRef[method](error.stack);
  }
}

const devLogger = {
  /**
   * Log critical failures that should surface prominently during development.
   * @param {string} context
   * @param {string} message
   * @param {unknown} [error]
   */
  critical(context, message, error = null) {
    logWith('error', 'CRITICAL', context, message, error);
  },

  /**
   * Log recoverable errors.
   * @param {string} context
   * @param {string} message
   * @param {unknown} [error]
   */
  error(context, message, error = null) {
    logWith('error', 'ERROR', context, message, error);
  },

  /**
   * Log warnings for degraded behaviour.
   * @param {string} context
   * @param {string} message
   * @param {unknown} [error]
   */
  warn(context, message, error = null) {
    logWith('warn', 'WARN', context, message, error);
  },

  /**
   * Informational logging primarily for diagnostics during development.
   * @param {string} context
   * @param {string} message
   */
  info(context, message) {
    if (!consoleRef || typeof consoleRef.info !== 'function') {
      return;
    }
    consoleRef.info(formatMessage('INFO', context, message));
  },

  /**
   * Track lifecycle events with optional metadata.
   * @param {string} event
   * @param {unknown} [details]
   */
  lifecycle(event, details = null) {
    if (!consoleRef || typeof consoleRef.log !== 'function') {
      return;
    }
    const suffix = details != null ? ` ${safeStringify(details)}` : '';
    consoleRef.log(`[LIFECYCLE] ${event}${suffix}`);
  },
};

const prodLogger = {
  critical: noop,
  error: noop,
  warn: noop,
  info: noop,
  lifecycle: noop,
};

export const MinimalErrorTracker = isProduction ? prodLogger : devLogger;

export const __TEST_INTERNALS__ = {
  safeStringify,
  isProduction,
};
